<template>
  <v-btn
    :class="buttonClass"
    :id="id ? `btn_${id}` : 'btn'"
    :aria-label="ariaLabel || label"
    :variant="variant == 'filled' ? 'flat' : variant"
    :disabled="disabled"
    @click="handleClick"
    :append-icon="appendIcon"
    :width="width"
    :style="{
      backgroundColor: backgroundColor,
      height: height,
      fontSize: textSize,
    }"
  >
    {{ label }}
  </v-btn>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const emits = defineEmits(['click'])

const props = defineProps({
  id: { type: String, default: '' },
  label: { type: String, required: true },
  ariaLabel: { type: String, default: null },
  variant: {
    type: String,
    default: 'outlined',
    validator: (value) => ['outlined', 'filled', 'text'].includes(value),
  },
  disabled: { type: Boolean, default: false },
  styleClass: { type: String, default: null },
  appendIcon: { type: String, default: null },
  backgroundColor: { type: String, default: '' },
  width: { type: String, default: '' },
  height: { type: String, default: '' },
  textSize: { type: String, default: '' },
  href: { default: null },
})

const buttonClass = computed(() => ({
  [props.styleClass]: !!props.styleClass,
  [`btn--${props.variant}`]: true,
  disabled: props.disabled,
  ['with-icon']: !!props.appendIcon,
}))

const handleClick = (event) => {
  if (!props.disabled) {
    if (props.href) {
      router.push(props.href)
    }
    emits('click', event)
  }
}
</script>

<style lang="scss" scoped>
@use '@/styles/colors' as c;

.v-btn {
  padding-left: 24px;
  padding-right: 24px;
  text-transform: none;

  &.with-icon {
    padding-right: 16px;
  }

  &.btn--filled {
    background-color: var(--kds-primary);
    color: var(--kds-on-primary);
    border-color: unset;

    &:hover {
      background:
        linear-gradient(0deg, var(--kds-on-primary-container-12) 0%, var(--kds-on-primary-container-12) 100%),
        var(--kds-primary);
      box-shadow: 0 1px 3px 1px rgba(0, 29, 52, 0.3);
    }

    &:focus {
      background:
        linear-gradient(0deg, var(--kds-on-primary-container-12) 0%, var(--kds-on-primary-container-12) 100%),
        var(--kds-primary);
      border: unset;
    }

    &:active {
      background:
        linear-gradient(0deg, var(--kds-on-primary-container-8) 0%, var(--kds-on-primary-container-8) 100%),
        var(--kds-primary);
      box-shadow: 0 4px 5.5px 1px rgba(0, 29, 52, 0.4) inset;
    }

    &.disabled {
      color: rgba(0, 24, 73, 0.38);
      background: var(--kds-on-surface-12);
    }

    &-success {
      background-color: var(--kds-success);
      color: var(--kds-on-success);
      border-color: unset;

      &:hover {
        background: linear-gradient(0deg, var(--kds-on-primary-8) 0%, var(--kds-on-primary-8) 100%), var(--kds-success);
        box-shadow: 0 1px 3px 1px rgba(0, 29, 52, 0.3);
      }

      &:focus {
        background:
          linear-gradient(0deg, var(--kds-on-primary-12) 0%, var(--kds-on-primary-12) 100%), var(--kds-success);
        border: unset;
      }

      &:focus-visible {
        outline: 1px solid var(--kds-focus);
        border-radius: 2px;
        outline-offset: 2px;
      }

      @supports not selector(:focus-visible) {
        &:focus {
          outline: 1px solid var(--kds-focus);
          border-radius: 2px;
          outline-offset: 2px;
        }
      }

      &:active {
        background: linear-gradient(0deg, var(--kds-on-primary-8) 0%, var(--kds-on-primary-8) 100%), var(--kds-success);
        box-shadow: 0 4px 5.5px 1px rgba(0, 29, 52, 0.4) inset;
      }

      &.disabled {
        color: rgba(0, 24, 73, 0.38);
        background: var(--kds-on-surface-12);
      }
    }

    &-danger {
      background-color: var(--kds-error);
      color: var(--kds-on-error);
      border-color: unset;

      &:hover {
        background:
          linear-gradient(0deg, var(--kds-on-error-container-12) 0%, var(--kds-on-error-container-12) 100%),
          var(--kds-error);
        box-shadow: 0 1px 3px 1px rgba(0, 29, 52, 0.3);
      }

      &:focus {
        background:
          linear-gradient(0deg, var(--kds-on-error-container-12) 0%, var(--kds-on-error-container-12) 100%),
          var(--kds-error);
        border: unset;
      }

      &:focus-visible {
        outline: 1px solid var(--kds-focus);
        border-radius: 2px;
        outline-offset: 2px;
      }

      @supports not selector(:focus-visible) {
        &:focus {
          outline: 1px solid var(--kds-focus);
          border-radius: 2px;
          outline-offset: 2px;
        }
      }

      &:active {
        background:
          linear-gradient(0deg, var(--kds-on-error-container-8) 0%, var(--kds-on-error-container-8) 100%),
          var(--kds-error);
        box-shadow: 0 4px 5.5px 1px rgba(0, 29, 52, 0.4) inset;
      }

      &.disabled {
        color: rgba(0, 24, 73, 0.38);
        background: var(--kds-on-surface-12);
      }
    }
  }

  &.btn--outlined {
    color: var(--kds-primary);
    border-color: var(--kds-outline);
    background-color: var(--kds-background);

    &:hover {
      border: 1px solid var(--kds-outline);
      background: var(--kds-primary-12);
    }

    &:focus {
      background: var(--kds-primary-12);
      border: 1px solid var(--kds-focus);
    }

    &:active {
      border: 1px solid var(--kds-outline);
      background: var(--kds-primary-16);
    }

    &.disabled {
      color: rgba(0, 24, 73, 0.38);
      border: 1px solid var(--kds-on-surface-12);
    }

    &-success {
      color: var(--kds-success);
      border-color: var(--kds-outline);
      background-color: var(--kds-background);

      &:hover {
        border: 1px solid var(--kds-outline);
        background: var(--kds-on-secondary-container-12);
      }

      &:focus {
        background: var(--kds-on-secondary-container-12);
        border: 1px solid var(--kds-focus);
      }

      &:active {
        border: 1px solid var(--kds-outline);
        background: var(--kds-on-secondary-container-16);
      }

      &.disabled {
        color: rgba(0, 24, 73, 0.38);
        border: 1px solid var(--kds-on-surface-12);
      }
    }

    &-danger {
      color: var(--kds-error);
      border-color: var(--kds-outline);
      background-color: var(--kds-background);

      &:hover {
        border: 1px solid var(--kds-outline);
        background: var(--kds-error-12);
      }

      &:focus {
        background: var(--kds-error-12);
        border: 1px solid var(--kds-focus);
      }

      &:active {
        border: 1px solid var(--kds-outline);
        background: var(--kds-error-16);
      }

      &.disabled {
        color: rgba(0, 24, 73, 0.38);
        border: 1px solid var(--kds-on-surface-12);
      }
    }
  }

  &.btn--text {
    color: var(--kds-primary);
    background-color: transparent;
    border: 1px solid transparent;

    &:hover {
      border: 1px solid var(--kds-outline-variant);
      background: var(--kds-primary-12);
    }

    &:focus {
      background: var(--kds-primary-12);
      border: 1px solid var(--kds-focus);
    }

    &:active {
      border: 1px solid var(--kds-outline);
      background: var(--kds-primary-16);
    }

    &.disabled {
      color: rgba(0, 24, 73, 0.38);
    }

    &-success {
      color: var(--kds-success);
      background-color: transparent;
      border: 1px solid transparent;

      &:hover {
        border: 1px solid var(--kds-outline-variant);
        background: var(--kds-on-secondary-container-12);
      }

      &:focus {
        background: var(--kds-on-secondary-container-12);
        border: 1px solid var(--kds-focus);
      }

      &:active {
        border: 1px solid var(--kds-outline);
        background: var(--kds-on-secondary-container-16);
      }

      &.disabled {
        color: rgba(0, 24, 73, 0.38);
      }
    }

    &-danger {
      color: var(--kds-error);
      background-color: transparent;
      border: 1px solid transparent;

      &:hover {
        border: 1px solid var(--kds-outline-variant);
        background: var(--kds-error-12);
      }

      &:focus {
        background: var(--kds-error-12);
        border: 1px solid var(--kds-focus);
      }

      &:active {
        border: 1px solid var(--kds-outline);
        background: var(--kds-error-16);
      }

      &.disabled {
        color: rgba(0, 24, 73, 0.38);
      }
    }
  }

  &:focus-visible,
  &:hover,
  &:active {
    outline: 1px solid var(--kds-focus);
    border-radius: 2px;
    outline-offset: 2px;
  }

  @supports not selector(:focus-visible) {
    &:focus {
      outline: 1px solid var(--kds-focus);
      border-radius: 2px;
      outline-offset: 2px;
    }
  }
}
</style>
